import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from 'providers/AuthProvider';
import { useWebsite } from 'providers/WebsiteProvider';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { PiEnvelopeDuotone, PiKeyDuotone, PiLockKeyDuotone, PiQuestionDuotone, PiSignInDuotone, PiUserPlusDuotone } from 'react-icons/pi';
import ForgotPasswordModal from '../components/ForgetPasswordModal';

export default function LoginPage(props) {
    return (
        <div className={'relative w-full flex-1 flex flex-col items-center justify-center gradient-blue px-4 py-10 md:px-site ' + (props.className || '')}>
            <div className='absolute w-full h-full top-0 left-0 bg-cover bg-center bg-no-repeat pointer-events-none' style={{ backgroundImage: 'url(/assets/images/background/pattern-opacity.png)' }} />
            <div className='w-full max-w-boxed flex flex-col items-center'>
                <Login />
            </div>
        </div>
    );
}

function Login(props) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [progress, setProgress] = useState(false);
    const [error, setError] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { login, token } = useAuth();
    const { setProfileOpen } = useWebsite();
    const passwordRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const inputClassName = 'w-full pl-12 pr-4 py-3.5 sm:py-4 border border-gray-300 rounded-lg outline-none text-sm transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100';
    const primaryButtonClassName = 'gradient-blue rounded-lg text-white py-3.5 sm:py-4 font-semibold flex items-center justify-center gap-2 transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-xl active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0';
    const registerButtonClassName = 'gradient-yellow rounded-lg text-white py-3.5 sm:py-4 font-semibold flex items-center justify-center gap-2 transition hover:-translate-y-0.5 hover:brightness-110 hover:shadow-xl active:translate-y-0';

    const handleLogin = () => {
        login(email, password, () => setProgress(true), () => {
            setProgress(false);
            setError(true);
        }).catch(() => {
            setProgress(false);
            setError(true);
        });
    };

    useEffect(() => {
        if (token) {
            setProgress(false);
            setProfileOpen(window.matchMedia('(min-width: 1024px)').matches);

            const requestedReturnTo = location.state?.returnTo;
            const hasSafeReturnTo = typeof requestedReturnTo === 'string'
                && requestedReturnTo.startsWith('/')
                && !requestedReturnTo.startsWith('//');
            const returnTo = hasSafeReturnTo ? requestedReturnTo : '/diakmunka';
            const requestedApplyJobId = Number(location.state?.applyJobId);
            const returnState = {
                ...(location.state?.resumeCheckout ? { resumeCheckout: true } : {}),
                ...(Number.isInteger(requestedApplyJobId) && requestedApplyJobId > 0
                    ? { applyJobId: requestedApplyJobId }
                    : {}),
            };

            navigate(returnTo, {
                replace: true,
                state: Object.keys(returnState).length ? returnState : null,
            });
        }
    }, [token, navigate, setProfileOpen, location.state]);

    return (
        <div className='flex flex-col justify-between gap-4 w-full max-w-[520px] z-10'>

            {/* <div className='bg-white w-full rounded-3xl shadow-2xl px-8 py-5 z-10 mx-4'>
              
            </div> */}

            <div className='flex flex-col gap-4 px-5 sm:px-8 pt-7 pb-14 mx-0 sm:mx-6 -mb-12 bg-white rounded-3xl'>

                <div className='flex flex-col gap-2 mb-2'>
                    <div className='font-display font-black text-4xl sm:text-5xl'>Belépés</div>
                    <div className='flex items-center gap-2 text-sm opacity-75'>
                        <PiKeyDuotone className='text-base text-gray-400' />
                        Jelentkezz be a Fürge Diák fiókodba
                    </div>
                </div>

                <div className='relative'>
                    <PiEnvelopeDuotone className='absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none' />
                    <input
                        type='email' placeholder='E-mail cím' value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={inputClassName}
                        onKeyDown={(e) => { if (e.key === 'Enter') passwordRef.current.focus(); }} />
                </div>

                <div className='relative'>
                    <PiLockKeyDuotone className='absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none' />
                    <input
                        ref={passwordRef} type='password' placeholder='Jelszó' value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={inputClassName}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }} />
                </div>

                <button onClick={handleLogin} disabled={progress} className={primaryButtonClassName} >
                    <PiSignInDuotone className='text-xl' />
                    {progress ? 'Belépés...' : 'Belépés'}
                </button>

                {error && <div className='text-red-500 mt-2'>Hibás e-mail vagy jelszó</div>}

                <button className='flex flex-col items-center justify-center gap-1 text-center text-xs text-gray-600 transition hover:text-blue-500' onClick={() => setIsModalOpen(true)}>
                    <span className='font-semibold'>Elfelejtetted a jelszavad?</span>
                    <span className='flex items-center justify-center gap-1'>
                        Semmi gáz, igényelj új jelszót itt!
                        <PiQuestionDuotone className='text-lg' />
                    </span>
                </button>

            </div>

            <ForgotPasswordModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

            <div className='w-full flex flex-col gap-4 gradient-green rounded-3xl shadow-2xl text-white p-5 sm:p-8'>

                <div className='flex flex-col gap-2 items-center text-center'>
                    <div className='text-xs'>Új vagy nálunk, és még nem regisztráltál a Fürge Diák oldalára?</div>
                    <div className='font-display font-black text-3xl sm:text-4xl text-center'>Regisztrálj most!</div>
                </div>

                <Link to={'/regisztracio'} className={registerButtonClassName}>
                    <PiUserPlusDuotone className='text-xl' />
                    Regisztráció
                </Link>

            </div>

        </div>
    );
}
